import { Api } from '../../axios-config/AxiosConfig';
import { ApiException } from '../ApiException';

export type IErpUsado = 'GVcentris' | 'GVcollege' | 'Nenhum';
export interface IProduto {
  nome: string;
  idProduto: string;
  azureCliente: boolean;
  urlDoBackend: string;
}

export interface IComponents {
  url: string;
  cloudflare: boolean;
  corsGateway: boolean;
  principal?: boolean;
}

export interface ITenant {
  _id: string;
  cliente: string;
  idTenant: string;
  subDominio: string;

  ativo: boolean;
  erpUsado: IErpUsado;

  produtosHabilitados: IProduto[];

  loginServiceUrl: string;
  loginServiceIssuer: string;
  loginServiceAudiencia: string;
  loginServiceChavePublica: string;

  apiGvcollegeUrl?: string | null;
  apiGvcollegeVersao?: string;
  portalGVcollegeUrl?: string | null;

  apiGVcentrisUrl?: string | null;
  portalAcademicoGVcentrisUrl?: string | null;
  apiPortalAcademicoGVcentrisUrl?: string | null;
  erpCentrisUrl?: string | null;

  apiCEPKey?: string;
  apiMobileUrl?: string;
}

export interface IDadosVersaoApi {
  valor: string;
  dataConsulta: string;
  mensagem?: string | null
}

export interface IGetAllTenants {
  _id: string;
  cliente: string;
  idTenant: string;
  subDominio: string;

  ativo: boolean;
  erpUsado: IErpUsado;
  produtosHabilitados: IProduto[];

  urlDoPainel: string;

  loginServiceUrl: string;
  loginServiceIssuer: string;
  loginServiceAudiencia: string;
  loginServiceChavePublica: string;
  loginServiceVersion?: IDadosVersaoApi

  apiGvcollegeUrl?: string;
  apiGvcollegeVersao?: string;
  portalGVcollegeUrl?: string;
  apiGVcollegeVersion?: IDadosVersaoApi
  gvcollegeVersion?: IDadosVersaoApi;

  apiGVcentrisUrl?: string;
  portalAcademicoGVcentrisUrl?: string;
  apiPortalAcademicoGVcentrisUrl?: string;
  apiGVcentrisVersion?: IDadosVersaoApi;

  apiMobileVersion?: IDadosVersaoApi;

  apiCEPKey?: string;
  apiMobileUrl?: string;
}

export interface IProdutoHabilitado {
  idProduto: string;
  components: IComponents[];
  azureDoCliente: boolean;
  urlDoBackend: string;
}

export interface INovoProduto {
  idProduto: string;
  components: IComponents[];
  azureCliente: boolean;
  urlDoBackend: string;
  categoriaProduto?: 'moduloCentris' | 'moduloCollege' | 'saaS' | 'onPremise';
  nomeProduto: string;
}

export interface ITenantComProdutosHabilitados {
  _id: string;
  cliente: string;
  idTenant: string;
  subDominio: string;

  ativo: boolean;
  erpUsado: IErpUsado;
  produtosHabilitados: INovoProduto[];
  loginServiceUrl: string;
  loginServiceIssuer: string;
  loginServiceAudiencia: string;
  loginServiceChavePublica: string;
  loginServiceVersion?: IDadosVersaoApi;

  apiGvcollegeUrl?: string;
  apiGvcollegeVersao: string;
  portalGVcollegeUrl?: string;
  apiGVcollegeVersion?: IDadosVersaoApi;
  gvcollegeVersion?: IDadosVersaoApi;

  apiGVcentrisUrl?: string;
  portalAcademicoGVcentrisUrl?: string;
  apiPortalAcademicoGVcentrisUrl?: string;
  apiGVcentrisVersion?: IDadosVersaoApi;
  erpCentrisUrl?: string;

  apiCEPKey?: string;
  apiMobileUrl?: string;
  apiMobileVersion?: IDadosVersaoApi;
}

interface IDeleteLicencaPayload {
  apiUrl: string;
  loginServiceUrl: string;
}

const getAllTenants = async (
  page: string,
  pageSize: number,
  busca?: string,
  erpUsadoFilter?: string,
  produtos?: string[],
  ativo?: boolean,
  versaoERP?: string,
  sortBy?: string,
  order?: 'asc' | 'desc',
  signal?: AbortSignal,
): Promise<{ data: IGetAllTenants[]; totalCount: number }> => {
  try {
    let filterQuery = '';
    if (busca) {
      const escapedFilterValue = busca.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&');
      filterQuery = `&busca=${escapedFilterValue}`;
    }

    const queryProdutos =
      produtos?.map((produto) => `produtos=${produto}`).join('&') || '';

    let ativoFilter = '';
    if (ativo === true) ativoFilter = '&ativo=true';
    if (ativo === false) ativoFilter = '&ativo=false';

    const versaoERPFilter = versaoERP ? `&versaoERP=${versaoERP}` : '';
    const sortByFilter = sortBy ? `&sortBy=${sortBy}` : '';
    const orderFilter = order ? `&order=${order}` : '';

    const queryString =
      `/Tenants?page=${page}&pageSize=${pageSize}${filterQuery}&erpUsadoFilter=${erpUsadoFilter}` +
      (queryProdutos ? `&${queryProdutos}` : '') +
      ativoFilter +
      versaoERPFilter +
      sortByFilter +
      orderFilter;

    const response = await Api.get(queryString, { signal } as any);

    const totalCount = Number(response.headers['x-total-count']) || 0;

    if (response.data) {
      return {
        data: response.data as IGetAllTenants[],
        totalCount,
      };
    }
    throw new ApiException('Não foi possível mostrar os dados');
  } catch (error: any) {
    throw new ApiException(
      error?.response?.data?.errors?.default || 'Erro ao buscar tenants',
    );
  }
};

const getTenantsById = async (
  id: string | number,
): Promise<ITenantComProdutosHabilitados | ApiException> => {
  try {
    const { data } = await Api.get(`/Tenants/${id}`);
    if (data) {
      return data as ITenantComProdutosHabilitados;
    } else {
      return new ApiException('Não foi possível mostrar os dados');
    }
  } catch (error: any) {
    console.error(error);
    return new ApiException(error.data.data.errors.default);
  }
};

const deleteTenantsById = async (
  id: string | number,
): Promise<void | ApiException> => {
  try {
    await Api.delete(`/Tenants/${id}`);
    return;
  } catch (error: any) {
    console.error(error);
    return new ApiException(error.data.data.errors.default);
  }
};

const deleteLicenca = async (
  apiUrl: string,
  loginServiceUrl: string,
): Promise<void | ApiException> => {
  try {
    const payload: IDeleteLicencaPayload = {
      apiUrl,
      loginServiceUrl,
    };
    await Api.delete('/LicencaCac', {
      data: payload,
    });
    return;
  } catch (error: any) {
    console.error('Erro ao deletar licença:', error);
    return new ApiException(
      error?.response?.data?.errors?.default || 'Erro ao deletar licença',
    );
  }
};
const createTenants = async (
  dados: ITenant,
): Promise<string | ApiException> => {
  try {
    const { data } = await Api.post('/Tenants', dados);
    if (data) {
      return data as string;
    }
    return new ApiException('Erro ao criar registro');
  } catch (error: any) {
    console.error(error);

    return new ApiException(
      error.data.data.errors.default ||
        error.data.data.errors.cliente ||
        error.data.data.errors.subDominio ||
        error.data.data.errors.idTenant ||
        error.data.data.errors.produtosHabilitados ||
        error.data.data.apiGVcentrisUrl ||
        error.data.data.apiGvcollegeUrl ||
        error.data.data.apiPortalAcademicoGVcentrisUrl ||
        error.data.data.portalAcademicoGVcentrisUrl ||
        error.data.data.portalGVcollegeUrl,
    );
  }
};

const updateTenantsById = async (
  id: string | number,
  dados: ITenant,
): Promise<void | ApiException> => {
  try {
    await Api.put(`/Tenants/${id}`, dados);
    return;
  } catch (error: any) {
    console.error(error.data.data.apiGvcollegeUrl);
    return new ApiException(
      error.data.data.errors?.default ||
        error.data.data.errors?.cliente ||
        error.data.data.errors?.subDominio ||
        error.data.data.errors?.idTenant ||
        error.data.data.errors?.produtosHabilitados ||
        error.data.data.data?.apiGVcentrisUrl ||
        error.data.data.data?.apiGvcollegeUrl ||
        error.data.data.data?.apiPortalAcademicoGVcentrisUrl ||
        error.data.data.data?.portalAcademicoGVcentrisUrl ||
        error.data.data.data?.portalGVcollegeUrl,
    );
  }
};

export const TenantsService = {
  updateTenantsById,
  deleteTenantsById,
  getTenantsById,
  getAllTenants,
  createTenants,
  deleteLicenca,
};
